import type { CaraFloatingOptions, CaraInstance } from '@wix/cara-sdk';
import { createCrossTabLock, type CrossTabLock } from '@wix/wix-chatbot-common/browser';
import { CARA_CHATBOT_FULLPAGE_HOST } from '@wix/wix-help-widget-common/types';
import { v4 as uuidv4 } from 'uuid';

import { getRuntimeDataForBi } from '../../bi/utils/getRuntimeDataForBi';
import { CARA_REFERRAL } from '../../common/hooks/useIsCaraSession';
import { widgetActions } from '../../common/reducers/widget';
import { selectEditorType } from '../../common/selectors/homepage';
import { selectSelectedSite } from '../../common/selectors/sites';
import { selectWidgetSessionId } from '../../common/selectors/widgetSession';
import { store } from '../../common/store';
import { loadChatbotStartChat } from '../../common/thunks/chatbotStartChat.thunk';
import { widgetSDK } from '../../sdk/widgetSDK';
import { emitter } from '../../util/emitter';
import { logger } from '../../util/logger';
import { renderModel } from '../../util/renderModel';
import {
  CARA_FULLPAGE_ANCHOR_DATA_HOOK,
  CARA_FULLPAGE_HOST_ATTRIBUTE,
  isFullPageCaraEnabled,
} from './isFullPageCaraEnabled';

const CARA_CONTACT_FLOW_NODE_ID = '5293f83f-910d-45bb-a776-c0b2e3a73b9d';
const FEEDBACK_MIN_CALL_DURATION_MS = 30_000;
const DESTROY_DELAY_MS = 100;
const NO_DRAG_BOUNDS = { top: 0, left: 0, right: 0, bottom: 0 };

let session: CaraInstance | null = null;
let lock: CrossTabLock | null = null;
let initialized = false;
let streamingStartTime: number | null = null;
let escalatingToContactFlow = false;

const isSessionActive = (): boolean => session !== null;

const isSessionBlocked = (): boolean => isSessionActive() || store.getState().widget.caraActiveInOtherTab;

const ensureLock = (): CrossTabLock => {
  if (!lock) {
    lock = createCrossTabLock({
      tabId: uuidv4(),
      isSessionActive,
      onStateChange: (active) => store.dispatch(widgetActions.setCaraActiveInOtherTab(active)),
    });
  }
  return lock;
};

const showGate = (): void => {
  store.dispatch(widgetActions.setCaraSessionInConversation(true));
  document.documentElement.setAttribute(CARA_FULLPAGE_HOST_ATTRIBUTE, '');
  store.dispatch(widgetActions.setCaraFullPageSessionActive(true));
};

const hideGate = (): void => {
  document.documentElement.removeAttribute(CARA_FULLPAGE_HOST_ATTRIBUTE);
  store.dispatch(widgetActions.setCaraFullPageSessionActive(false));
};

const abandonGate = (): void => {
  hideGate();
  store.dispatch(widgetActions.setCaraSessionInConversation(false));
};

const wasCallLongEnoughToRate = (): boolean =>
  streamingStartTime !== null && Date.now() - streamingStartTime >= FEEDBACK_MIN_CALL_DURATION_MS;

const requestPostCallFeedback = (): void => {
  if (escalatingToContactFlow) {
    store.dispatch(widgetActions.setCaraSurveyPending(true));
    return;
  }
  if (wasCallLongEnoughToRate()) {
    emitter.emit('caraCallEnded', undefined);
  }
};

const escalateToContactFlow = async (): Promise<void> => {
  if (escalatingToContactFlow) {
    return;
  }
  escalatingToContactFlow = true;
  try {
    const context = await widgetSDK.getContext();
    const state = store.getState();
    const selectedSite = selectSelectedSite(state);
    if (selectedSite?.id) {
      context.metaSiteId = selectedSite.id;
    }
    await store
      .dispatch(
        loadChatbotStartChat({
          context,
          editorType: selectEditorType(state),
          params: { nodeId: CARA_CONTACT_FLOW_NODE_ID, referral: CARA_REFERRAL },
        }),
      )
      .unwrap();
  } catch (error) {
    escalatingToContactFlow = false;
    throw error;
  }
};

const attachSessionLifecycle = (instance: CaraInstance): void => {
  instance.on('started', ({ streamingStartTime: startedAt }) => {
    if (session !== instance) {
      return;
    }
    streamingStartTime = startedAt;
  });

  instance.on('stopped', () => {
    if (session !== instance) {
      return;
    }
    session = null;
    lock?.clear();
    hideGate();
    requestPostCallFeedback();
    streamingStartTime = null;
    escalatingToContactFlow = false;

    setTimeout(() => instance.destroy(), DESTROY_DELAY_MS);
  });
};

export const startFullPageCaraSession = async ({
  sessionReferral,
  conversationId,
}: {
  sessionReferral: string;
  conversationId?: string;
}): Promise<void> => {
  if (isSessionBlocked()) {
    return;
  }

  let instance: CaraInstance | null = null;

  try {
    const state = store.getState();
    const { mountFloating } = await import('@wix/cara-sdk');
    const { metaSiteId } = await getRuntimeDataForBi(state);

    if (isSessionBlocked()) {
      return;
    }

    showGate();

    const gateOptions: CaraFloatingOptions = {
      gateAnchor: `[data-hook="${CARA_FULLPAGE_ANCHOR_DATA_HOOK}"]`,
      gateDragBounds: () => NO_DRAG_BOUNDS,
      onGateBack: () => session?.stop(),
      disableInGateFeedback: true,
      onContactFlowConnect: escalateToContactFlow,
    };

    instance = mountFloating(
      {
        contextPlatform: CARA_CHATBOT_FULLPAGE_HOST,
        conversationId: conversationId?.trim() || undefined,
        contextMsid: metaSiteId,
        selfServiceSessionId: selectWidgetSessionId(state),
        sessionReferral,
        user: {
          userId: renderModel.getUserId(),
          loggedInAccountId: renderModel.getAccountId(),
        },
      },
      gateOptions,
    );

    if (!instance) {
      abandonGate();
      return;
    }

    session = instance;
    ensureLock().write();
    attachSessionLifecycle(instance);

    await instance.start();
  } catch (error) {
    if (instance && session !== instance) {
      return;
    }
    logger.error({
      error: error instanceof Error ? error : new Error(String(error)),
      message: 'Failed to start the Cara full page session',
    });
    if (instance) {
      session = null;
      lock?.clear();
      instance.destroy();
    }
    abandonGate();
  }
};

export const initFullPageCara = (): void => {
  if (initialized || !isFullPageCaraEnabled()) {
    return;
  }
  initialized = true;

  const caraLock = ensureLock();

  window.addEventListener('storage', (event) => caraLock.handleStorageEvent(event));
  window.addEventListener('pagehide', () => caraLock.clear());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      caraLock.check();
    }
  });

  caraLock.subscribeToRemoteChanges();
  caraLock.check();
};
